/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useImperativeHandle, forwardRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentUserApiToken } from "../../slices/currentUser/currentUserSlice";
import {
    m11ReportContainerFoiChanged, m11ReportContainerResourceChanged,
    m11ReportSection03FoiChanged, m11ReportSection03ResourceChanged, selectM11ReportContainerFoi,
    selectM11ReportContainerResource, selectM11ReportSection03Foi, selectM11ReportSection03Resource,
    selectM11ResearchStudyResource
} from "../../slices/m11ReportAuthoringTool/m11ReportAuthoringToolSlice";
import jsonEquals from "../../functions/jsonEquals";
import getIdFromReference from "../../functions/getIdFromReference";
import loadResource from "../m11ReportFunctions/loadResource";
import TextDivEntry from "./TextDivEntry";
import generateM11Section3Composition from "../m11ReportFunctions/m11Section3Generator";
import ArrayEntry from "../../datatypeComponents/ArrayEntry";
import saveM11Changes from "../../functions/saveM11ChangesAPI";
import { selectM11ResearchStudyFoi } from "../../slices/m11StepCount/m11StepCountSlice";
import { validateStep } from "../../functions/validateM11ReportAPI";

const saveObjectivesAndEstimandsChanges = async (dispatch, apiToken, m11ResearchStudyFoi, section3State,
    setAlertModalState, setStartingSection3State) => {

    let changesStates = {
        section3State: section3State
    };
    let response = await saveM11Changes(apiToken, 12, m11ResearchStudyFoi, changesStates);
    if (response?.success) {
        if (response.m11ReportContainerResource) {
            dispatch(m11ReportContainerResourceChanged(response.m11ReportContainerResource));
        }
        if (response.m11ReportSection03Resource) {
            dispatch(m11ReportSection03ResourceChanged(response.m11ReportSection03Resource));
        }
        setStartingSection3State(JSON.parse(JSON.stringify(section3State)));
    } else {
        if (response.error) {
            alert(response.error);
        }
    }
};

const M11Step012ObjectivesAndEstimands = forwardRef(({ theM11ResearchStudyFoi, setAlertModalState }, ref) => {

    const dispatch = useDispatch();
    const apiToken = useSelector(selectCurrentUserApiToken);
    const m11ResearchStudyResource = useSelector(selectM11ResearchStudyResource);
    const m11ReportContainerFoi = useSelector(selectM11ReportContainerFoi);
    const m11ReportContainerResource = useSelector(selectM11ReportContainerResource);
    const m11ReportSection03Foi = useSelector(selectM11ReportSection03Foi);
    const m11ReportSection03Resource = useSelector(selectM11ReportSection03Resource);
    const m11ResearchStudyFoi = useSelector(selectM11ResearchStudyFoi);

    const [section3State, setSection3State] = useState({
        "sectionPrimary1status": "empty", "sectionPrimary1Xhtml": "",
        "otherPrimaryObjectives": [],
        "secondaryObjectives": [],
        "exploratoryObjectives": []
    });
    const [startingSection3State, setStartingSection3State] = useState({
        "sectionPrimary1status": "empty", "sectionPrimary1Xhtml": "",
        "otherPrimaryObjectives": [],
        "secondaryObjectives": [],
        "exploratoryObjectives": []
    });
    const [initialCalculationNeeded, setInitialCalculationNeeded] = useState(false);

    useImperativeHandle(ref, () => ({
        unsavedChanges: (m11ResearchStudyResource && !jsonEquals(section3State, startingSection3State)),
        async saveAllChanges() {
            await saveObjectivesAndEstimandsChanges(dispatch, apiToken, m11ResearchStudyFoi, section3State,
                setAlertModalState, setStartingSection3State);
        }
    }));

    useEffect(() => {
        if (m11ResearchStudyResource?.id) {
            let m11ReportFoi = "";
            if (m11ResearchStudyResource.relatesTo?.length > 0) {
                for (let relatesTo of m11ResearchStudyResource.relatesTo) {
                    if (relatesTo.type?.coding?.[0]?.code === "supported-with" && relatesTo.type.text === "M11 Report" &&
                        relatesTo.targetReference) {
                        m11ReportFoi = getIdFromReference(relatesTo.targetReference);
                    }
                }
            }
            if (m11ReportFoi && !isNaN(m11ReportFoi) && m11ReportFoi !== m11ReportContainerFoi) {
                dispatch(m11ReportContainerFoiChanged(m11ReportFoi));
            } else {
                setInitialCalculationNeeded(true);
            }
        }
    }, [m11ResearchStudyResource]);

    useEffect(() => {
        if (initialCalculationNeeded === true) {
            let startingData = {
                "sectionPrimary1status": "empty", "sectionPrimary1Xhtml": "",
                "otherPrimaryObjectives": [],
                "secondaryObjectives": [],
                "exploratoryObjectives": []
            };
            let section3Json = generateM11Section3Composition(m11ResearchStudyResource, {});
            if (section3Json?.section?.[0]?.section?.[0]?.section?.[0]?.text) {
                startingData.sectionPrimary1status = section3Json.section[0].section[0].section[0].text.status;
                startingData.sectionPrimary1Xhtml = section3Json.section[0].section[0].section[0].text.div;
                if (section3Json.section[0].section[0].section.length > 1) {
                    let otherPrimaryObjectives = section3Json.section[0].section[0].section.slice(1);
                    startingData.otherPrimaryObjectives = otherPrimaryObjectives.map(section => section.text.div);
                }
                if (section3Json.section[0].section[1].section?.length > 0) {
                    startingData.secondaryObjectives = section3Json.section[0].section[1].section.map(section => section.text.div);
                }
                if (section3Json.section[0].section[2].section?.length > 0) {
                    startingData.exploratoryObjectives = section3Json.section[0].section[2].section.map(section => section.text.div);
                }
                if (startingData.sectionPrimary1status === "empty") {
                    alert("Problem with empty status for required text for Primary Objective 1");
                } else {
                    startingData.loaded = true;
                }
            } else {
                alert("Problem generating the M11ReportSection03 Resource");
            }
            setSection3State(startingData);
            setStartingSection3State({
                "sectionPrimary1status": "empty", "sectionPrimary1Xhtml": "",
                "otherPrimaryObjectives": [],
                "secondaryObjectives": [],
                "exploratoryObjectives": []
            });
        }
    }, [initialCalculationNeeded]);

    useEffect(() => {
        if (m11ReportContainerFoi && !isNaN(m11ReportContainerFoi)) {
            loadResource(m11ReportContainerFoi, "M11ReportContainer", apiToken, dispatch);
        }
    }, [m11ReportContainerFoi]);

    useEffect(() => {
        if (m11ReportContainerResource && Object.keys(m11ReportContainerResource).length > 0) {
            if (m11ReportContainerResource.section?.[3]?.entry?.length) {
                let m11ReportSection003Foi = getIdFromReference(m11ReportContainerResource.section[3].entry[0]);
                if (m11ReportSection003Foi && !isNaN(m11ReportSection003Foi) && m11ReportSection003Foi !== m11ReportSection03Foi) {
                    dispatch(m11ReportSection03FoiChanged(m11ReportSection003Foi));
                } else {
                    setInitialCalculationNeeded(true);
                }
            } else {
                setInitialCalculationNeeded(true);
            }
        }
    }, [m11ReportContainerResource]);

    useEffect(() => {
        if (m11ReportSection03Foi && !isNaN(m11ReportSection03Foi)) {
            loadResource(m11ReportSection03Foi, "M11ReportSection03", apiToken, dispatch);
        }
    }, [m11ReportSection03Foi]);

    useEffect(() => {
        if (m11ReportSection03Resource && Object.keys(m11ReportSection03Resource).length > 0) {
            if (m11ReportSection03Resource.section?.[0]?.section?.[0]?.section?.[0]?.text?.div &&
                m11ReportSection03Resource.section[0].section[0].section[0].text.status !== "empty") {
                let startingData = {
                    "sectionPrimary1status": m11ReportSection03Resource.section[0].section[0].section[0].text.status,
                    "sectionPrimary1Xhtml": m11ReportSection03Resource.section[0].section[0].section[0].text.div,
                    "otherPrimaryObjectives": [],
                    "secondaryObjectives": [],
                    "exploratoryObjectives": [],
                    "loaded": true
                };
                if (m11ReportSection03Resource.section[0].section[0].section.length > 1) {
                    startingData.otherPrimaryObjectives = m11ReportSection03Resource.section[0].section[0].section.slice(1).map(section => section.text.div);
                }
                if (m11ReportSection03Resource.section[0].section[1].section?.length > 0) {
                    startingData.secondaryObjectives = m11ReportSection03Resource.section[0].section[1].section.map(section => section.text.div);
                }
                if (m11ReportSection03Resource.section[0].section[2].section?.length > 0) {
                    startingData.exploratoryObjectives = m11ReportSection03Resource.section[0].section[2].section.map(section => section.text.div);
                }
                if (startingData.sectionPrimary1status === "empty") {
                    alert("Problem with empty status for required text for Primary Objective 1");
                } else {
                    startingData.loaded = true;
                }
                setSection3State(startingData);
                setStartingSection3State(startingData);
            } else {
                setInitialCalculationNeeded(true);
            }
        }
    }, [m11ReportSection03Resource]);

    return <div>
        <h3>Review Section 3 Trial Objectives and Associated Estimands</h3>
        {!(m11ReportSection03Resource?.id || m11ReportContainerResource?.id) &&
            <h1 style={{ color: "red" }}>Please complete Step 5 (Complete Title Page) before filling in M11 Report Section details.</h1>
        }
        {section3State.loaded && <>
            <h4 style={{ marginBottom: "0px" }}>Revise Section 3.1.1 Primary Objective 1 summary as needed.</h4>
            <TextDivEntry elementName="sectionPrimary1Xhtml"
                startingValue={section3State.sectionPrimary1Xhtml}
                setResourceState={setSection3State} />
            {section3State.otherPrimaryObjectives.length > 0 ?
                <div>
                    <h4>Revise Section 3.1.X Primary Objective X summaries as needed.</h4>
                    <ArrayEntry datatype="TextDiv" elementName='secondaryObjectives'
                        doNotShowFieldLabel={true}
                        startCollapsed={true} startEmptyArrayClosed={false}
                        deletable={true} fieldLabel={"Section 3.1.X Primary Objective"}
                        startingValue={section3State.secondaryObjectives || []}
                        setResourceState={setSection3State} />
                </div>
                :
                <h4>There are no additional Primary Objectives.  If there should be, Go To Step 9 (Enter Additional Objectives).</h4>
            }
            {section3State.secondaryObjectives.length > 0 ?
                <div>
                    <h4>Revise Section 3.2.X Secondary Objective X summaries as needed.</h4>
                    <ArrayEntry datatype="TextDiv" elementName='secondaryObjectives'
                        doNotShowFieldLabel={true}
                        startCollapsed={true} startEmptyArrayClosed={false}
                        deletable={true} fieldLabel={"Section 3.2.X Secondary Objective"}
                        startingValue={section3State.secondaryObjectives || []}
                        setResourceState={setSection3State} />
                </div>
                :
                <h4>There are no Secondary Objectives.  If there should be, Go To Step 10 (Enter Additional Objectives).</h4>
            }
            {section3State.exploratoryObjectives.length > 0 ?
                <div>
                    <h4>Revise Section 3.3.X Exploratory Objective X summaries as needed.</h4>
                    <ArrayEntry datatype="TextDiv" elementName='exploratoryObjectives'
                        doNotShowFieldLabel={true}
                        startCollapsed={true} startEmptyArrayClosed={false}
                        deletable={true} fieldLabel={"Section 3.3.X Exploratory Objective"}
                        startingValue={section3State.exploratoryObjectives || []}
                        setResourceState={setSection3State} />
                </div>
                :
                <h4>There are no Exploratory Objectives.  If there should be, Go To Step 11 (Enter Additional Objectives).</h4>
            }
        </>}
    </div >
});

const M11Step012ObjectivesAndEstimandsHelperText = <div>
    <h3>Review Section 3 Trial Objectives and Associated Estimands</h3>
    <div style={{ textAlign: "left" }}>
        <p>Review the auto-generated summary from previously submitted data. This will be the content for Section 3 Trial Objectives and Associated Estimands.</p>
        <p>Revise content as desired.</p>
        <div style={{ marginLeft: "24px" }}>
            <p>If there is data for an objective, you can revise the data directly in what will be used for the M11 Report.</p>
            <p>If you desire to change the structured data, Go To Step 7 (Enter First Primary Objective) or Step 8 (Enter Additional Objectives).</p>
            <p>If you need to add an objective, Go To Step 8 (Enter Additional Objectives).</p>
        </div>
        <p>Click the 'Save All Changes' button.</p>
    </div>
</div>
// eslint-disable-next-line no-empty-pattern
const M11Step012Section3ValidationCheck = ({ }) => {

  const apiToken = useSelector(selectCurrentUserApiToken);
  const m11ResearchStudyFoi = useSelector(selectM11ResearchStudyFoi);

  const [validationCheckState, setValidationCheckState] = useState({});

  useEffect(() => {
    if (m11ResearchStudyFoi) {
      validateStep(apiToken, 12, m11ResearchStudyFoi,
        setValidationCheckState)
    }
  }, [m11ResearchStudyFoi]);

  return <div>{validationCheckState?.success && <div>
        <h4>Step 12 (Review Section 3 Trial Objectives and Associated Estimands) {validationCheckState.step12numberOfChecks} issues checked. {validationCheckState.step12numberPassed} passed. {validationCheckState.step12numberFailed} failed.</h4>
        <p>Section 3 heading is 'TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS': {validationCheckState.resultCheckSection3Heading}</p>
        <p>Section 3 TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS does not have text: {validationCheckState.resultCheckSection3Text}</p>
        <p>Section 3.1 heading is 'Primary Objective(s) and Associated Estimand(s)': {validationCheckState.resultCheckSection31Heading}</p>
        <p>Section 3.1 Primary Objective(s) and Associated Estimand(s) does not have text: {validationCheckState.resultCheckSection31Text}</p>
        <p>Section 3.1.1 heading is 'Primary Objective 1' or 'Primary Objective' if only one primary objective: {validationCheckState.resultCheckSection311Heading}</p>
        <p>Section 3.1.1 Primary Objective 1 has content: {validationCheckState.resultCheckSection311Content}</p>
        <p>Section 3.1.1 Primary Objective 1 content includes 'Estimand Characteristic': {validationCheckState.resultCheckSection311ContentEstimandCharacteristic}</p>
        <p>Section 3.1.1 Primary Objective 1 content includes 'Description': {validationCheckState.resultCheckSection311ContentDescription}</p>
        <p>Section 3.1.1 Primary Objective 1 content includes 'Endpoint': {validationCheckState.resultCheckSection311ContentEndpoint}</p>
        <p>If present, Section 3.1.X heading is 'Primary Objective X': {validationCheckState.resultCheckSection31XHeading}</p>
        <p>If present, Section 3.1.X Primary Objective X has content: {validationCheckState.resultCheckSection31XContent}</p>
        <p>If present, Section 3.1.X Primary Objective X content includes 'Estimand Characteristic': {validationCheckState.resultCheckSection31XContentEstimandCharacteristic}</p>
        <p>If present, Section 3.1.X Primary Objective X content includes 'Description': {validationCheckState.resultCheckSection31XContentDescription}</p>
        <p>If present, Section 3.1.X Primary Objective X content includes 'Endpoint': {validationCheckState.resultCheckSection31XContentEndpoint}</p>
        <p>Section 3.2 heading is 'Secondary Objective(s) and Associated Estimand(s)': {validationCheckState.resultCheckSection32Heading}</p>
        <p>Section 3.2 Secondary Objective(s) and Associated Estimand(s) has content of 'Not applicable' or subheadings: {validationCheckState.resultCheckSection32Content}</p>
        <p>If present, Section 3.2.X heading is 'Secondary Objective X' or 'Secondary Objective' if only one secondary objective: {validationCheckState.resultCheckSection32XHeading}</p>
        <p>If present, Section 3.2.X Secondary Objective X has content: {validationCheckState.resultCheckSection32XContent}</p>
        <p>If present, Section 3.2.X Secondary Objective X content includes 'Estimand Characteristic': {validationCheckState.resultCheckSection32XContentEstimandCharacteristic}</p>
        <p>If present, Section 3.2.X Secondary Objective X content includes 'Description': {validationCheckState.resultCheckSection32XContentDescription}</p>
        <p>If present, Section 3.2.X Secondary Objective X content includes 'Endpoint': {validationCheckState.resultCheckSection32XContentEndpoint}</p>
        <p>Section 3.3 heading is 'Exploratory Objective(s)': {validationCheckState.resultCheckSection33Heading}</p>
        <p>Section 3.3 Exploratory Objective(s) has content of 'Not applicable' or subheadings: {validationCheckState.resultCheckSection33Content}</p>
        <p>If present, Section 3.3.X heading is 'Exploratory Objective X' or 'Exploratory Objective' if only one exploratory objective: {validationCheckState.resultCheckSection33XHeading}</p>
        <p>If present, Section 3.3.X Exploratory Objective X has content: {validationCheckState.resultCheckSection33XContent}</p>
        <p>If present, Section 3.3.X Exploratory Objective X content includes 'Estimand Characteristic': {validationCheckState.resultCheckSection33XContentEstimandCharacteristic}</p>
        <p>If present, Section 3.3.X Exploratory Objective X content includes 'Description': {validationCheckState.resultCheckSection33XContentDescription}</p>
        <p>If present, Section 3.3.X Exploratory Objective X content includes 'Endpoint': {validationCheckState.resultCheckSection33XContentEndpoint}</p>
  </div>}  </div>
}

export default M11Step012ObjectivesAndEstimands;
export { M11Step012ObjectivesAndEstimandsHelperText, M11Step012Section3ValidationCheck };